import { type AirActionTrackingLocation, useTrackEditedBoard } from '@air/analytics';
import { Boards } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { editBoardSuccessAction } from '~/store/centralizedBoard/actions';
import { callAirApi } from '~/swr-hooks/callAirApi';
import { useUpdateBoardInAllLists } from '~/utils/mutateUtils/AllBoards';

export type UsePrivateBoardsDeleteBoardCoverMutationParams = Parameters<typeof Boards.deleteCoverImage>[0] & {
  board?: Board;
  trackLocation: AirActionTrackingLocation;
};

export const usePrivateBoardsDeleteBoardCoverMutation = () => {
  const dispatch = useDispatch();
  const { updateBoardInAllLists } = useUpdateBoardInAllLists();
  const { trackEditedBoard } = useTrackEditedBoard();

  return useMutation({
    mutationFn: async (params: UsePrivateBoardsDeleteBoardCoverMutationParams) => {
      return callAirApi(() => Boards.deleteCoverImage(params));
    },

    onMutate: ({ board, boardId, trackLocation }) => {
      if (board) {
        const updatedBoard: Board = {
          ...board,
          coverImage: undefined,
        };

        updateBoardInAllLists(updatedBoard);
        dispatch(editBoardSuccessAction({ board: updatedBoard }));
      }

      trackEditedBoard({ boardId, location: trackLocation });
    },

    onError: (error, variables) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to delete board cover',
        metadata: {
          key: 'data',
          data: {
            boardId: variables.boardId,
            workspaceId: variables.workspaceId,
          },
        },
      });
    },
  });
};
